/**
 * AdminCampaignsPage — /admin/campaigns (Sprint 4)
 * Dashboard admin : compteurs + tabs (à étudier, ouvertes, seuil atteint, terminées)
 * + gestion précommandes (confirmer, annuler, éditer, manual).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Shirt, ShieldAlert, ArrowLeft, CheckCircle2, XCircle, RefreshCw, Send, PlayCircle, Check, X, Edit3, Package, Users, ChevronDown, ChevronUp,
} from "lucide-react";

const TABS = [
  { key: "to_review", label: "À étudier", statuses: ["submitted", "admin_review"] },
  { key: "preorder_open", label: "Précommandes ouvertes", statuses: ["preorder_open"] },
  { key: "threshold_reached", label: "Seuil atteint", statuses: ["threshold_reached"] },
  { key: "confirmed_offline", label: "Confirmées hors plateforme", statuses: ["confirmed_offline"] },
  { key: "fulfilled", label: "Terminées", statuses: ["fulfilled"] },
];

export default function AdminCampaignsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = (user?.roles || []).includes("admin");

  const [tab, setTab] = useState("to_review");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // campaign id
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [countsRes, listRes] = await Promise.all([
        api.get("/admin/campaigns/dashboard"),
        api.get("/admin/campaigns"),
      ]);
      setCounts(countsRes.data || {});
      setItems(listRes.data.items || []);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  }, [refreshKey]);
  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);

  const filteredItems = useMemo(() => {
    const target = TABS.find((t) => t.key === tab)?.statuses || [];
    return items.filter((c) => target.includes(c.status));
  }, [items, tab]);

  if (!user) return <div className="p-6"><Card className="p-6 text-center">Connexion requise.</Card></div>;
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="campaigns-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">Ce dashboard est réservé aux administrateurs.</p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/")}><ArrowLeft className="h-4 w-4 mr-1.5" />Retour</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Shirt className="h-6 w-6 text-[hsl(var(--aito-gold))]" />Campagnes AITO x Athlète
          </h1>
          <p className="text-xs text-muted-foreground">AITO.PF n'encaisse aucun paiement. Ce tableau centralise les précommandes.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setRefreshKey((k) => k + 1)}><RefreshCw className="h-3.5 w-3.5 mr-1" />Actualiser</Button>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-md border p-3 text-left transition ${tab === t.key ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/5" : "hover:bg-secondary/40"}`} data-testid={`campaigns-tile-${t.key}`}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{t.label}</div>
            <div className="text-2xl font-display font-bold">{counts[t.key] ?? 0}</div>
          </button>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`campaigns-tab-${t.key}`}>{t.label} ({counts[t.key] ?? 0})</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : filteredItems.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Aucune campagne dans cet onglet.</Card>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((c) => (
            <AdminCampaignCard key={c.id} c={c} expanded={expanded === c.id} onToggle={() => setExpanded(expanded === c.id ? null : c.id)} onChanged={() => setRefreshKey((k) => k + 1)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminCampaignCard({ c, expanded, onToggle, onChanged }) {
  const currentT = (c.tranches || []).find((t) => t.tranche_number === c.current_tranche_number);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionData, setDecisionData] = useState({ product_name: c.product_name || "", price_display: c.price_display || "", price_public_xpf: "", batch_size: c.batch_size || 20, review_note: "" });
  const [busy, setBusy] = useState(false);
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = expanded ?? localExpanded;
  const handleToggle = () => {
    if (onToggle) onToggle();
    setLocalExpanded((v) => !v);
  };

  const decision = async (dec) => {
    setBusy(true);
    try {
      await api.post(`/admin/campaigns/${c.id}/decision`, {
        decision: dec,
        review_note: decisionData.review_note || null,
        product_name: decisionData.product_name || null,
        price_display: decisionData.price_display || null,
        price_public_xpf: decisionData.price_public_xpf ? Number(decisionData.price_public_xpf) : null,
        batch_size: Number(decisionData.batch_size) || 20,
      });
      toast.success(`Décision : ${dec}`);
      setDecisionOpen(false);
      onChanged();
    } catch (e) { showError(e); }
    finally { setBusy(false); }
  };

  const openPreorder = async () => {
    if (!window.confirm("Ouvrir la première tranche (20 unités) et rendre la campagne publique ?")) return;
    try { await api.post(`/admin/campaigns/${c.id}/open-preorder`, {}); toast.success("Tranche ouverte"); onChanged(); }
    catch (e) { showError(e); }
  };

  const openNext = async () => {
    if (!window.confirm("Ouvrir une nouvelle tranche (20 unités) ?")) return;
    try { await api.post(`/admin/campaigns/${c.id}/open-next-tranche`, {}); toast.success("Nouvelle tranche ouverte"); onChanged(); }
    catch (e) { showError(e); }
  };

  const confirmOffline = async () => {
    if (!currentT || !window.confirm("Confirmer que cette tranche est prise en charge hors plateforme ?")) return;
    try { await api.post(`/admin/campaigns/${c.id}/tranches/${currentT.id}/confirm-offline`); toast.success("Confirmée hors plateforme"); onChanged(); }
    catch (e) { showError(e); }
  };

  return (
    <Card className="overflow-hidden" data-testid={`admin-campaign-${c.id}`}>
      <div className="p-4 flex items-start gap-3 flex-wrap">
        {c._athlete ? (
          <img src={resolveAthletePhoto(c._athlete)} alt="" className={`h-14 w-14 rounded-md shrink-0 ${c._athlete.photo_url ? "object-cover" : "object-cover bg-white"}`} />
        ) : <div className="h-14 w-14 rounded-md bg-secondary shrink-0 flex items-center justify-center"><Shirt className="h-6 w-6 text-muted-foreground" /></div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/athletes/${c._athlete?.slug || c._athlete?.id}`} className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-[hsl(var(--aito-lagoon))]">AITO x {c._athlete?.full_name}</Link>
            <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
          </div>
          <div className="font-display font-bold text-lg leading-tight">{c.title}</div>
          {currentT && <div className="text-sm mt-1">Tranche {currentT.tranche_number} : <strong>{currentT.confirmed_count || 0}/{currentT.batch_size || 20}</strong> T-shirts confirmés · {currentT.preorder_count || 0} intentions (unités)</div>}
        </div>
        <div className="flex gap-1 flex-wrap justify-end">
          {["submitted", "admin_review", "draft"].includes(c.status) && (
            <>
              <Button size="sm" onClick={() => setDecisionOpen(true)} data-testid={`campaign-review-${c.id}`}><Edit3 className="h-3.5 w-3.5 mr-1" />Étudier</Button>
            </>
          )}
          {c.status === "approved" && (
            <Button size="sm" onClick={openPreorder} className="bg-[hsl(var(--aito-lagoon))] text-black" data-testid={`campaign-open-${c.id}`}><PlayCircle className="h-3.5 w-3.5 mr-1" />Ouvrir la précommande</Button>
          )}
          {c.status === "threshold_reached" && (
            <Button size="sm" onClick={confirmOffline} data-testid={`campaign-confirm-offline-${c.id}`}><Check className="h-3.5 w-3.5 mr-1" />Confirmer hors plateforme</Button>
          )}
          {c.status === "confirmed_offline" && (
            <Button size="sm" variant="outline" onClick={openNext} data-testid={`campaign-next-tranche-${c.id}`}>Nouvelle tranche</Button>
          )}
          <Button size="sm" variant="ghost" onClick={handleToggle} data-testid={`campaign-toggle-${c.id}`}>
            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}Précommandes
          </Button>
        </div>
      </div>

      {isExpanded && <AdminPreordersPanel campaignId={c.id} onChanged={onChanged} />}

      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Étudier la demande</DialogTitle><DialogDescription>Préparez le produit avant validation. Les précommandes n'ouvrent pas immédiatement.</DialogDescription></DialogHeader>
          <div className="grid gap-3 py-2 text-sm">
            <div><Label>Nom produit</Label><Input value={decisionData.product_name} onChange={(e) => setDecisionData({ ...decisionData, product_name: e.target.value })} placeholder="T-shirt Édition JP 2027" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prix affiché</Label><Input value={decisionData.price_display} onChange={(e) => setDecisionData({ ...decisionData, price_display: e.target.value })} placeholder="4 500 F CFP / unité" /></div>
              <div><Label>Prix XPF (interne)</Label><Input type="number" value={decisionData.price_public_xpf} onChange={(e) => setDecisionData({ ...decisionData, price_public_xpf: e.target.value })} /></div>
            </div>
            <div><Label>Batch size</Label><Input type="number" value={decisionData.batch_size} onChange={(e) => setDecisionData({ ...decisionData, batch_size: e.target.value })} /></div>
            <div><Label>Note de revue</Label><Textarea rows={2} value={decisionData.review_note} onChange={(e) => setDecisionData({ ...decisionData, review_note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => decision("request_changes")} disabled={busy}>Demander modifs</Button>
            <Button variant="outline" onClick={() => decision("reject")} disabled={busy} className="text-destructive"><XCircle className="h-3.5 w-3.5 mr-1" />Refuser</Button>
            <Button onClick={() => decision("approve")} disabled={busy} className="bg-[hsl(var(--aito-lagoon))] text-black"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approuver</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AdminPreordersPanel({ campaignId, onChanged }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [preRes, statsRes] = await Promise.all([
        api.get(`/admin/campaigns/${campaignId}/preorders`),
        api.get(`/admin/campaigns/${campaignId}/stats`).catch(() => ({ data: null })),
      ]);
      setItems(preRes.data.items || []);
      setStats(statsRes?.data || null);
    } catch (e) { showError(e); }
    finally { setLoading(false); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => { try { await api.post(`/admin/campaigns/preorders/${id}/confirm`); toast.success("Confirmée"); load(); onChanged(); } catch (e) { showError(e); } };
  const cancel = async (id) => { const reason = window.prompt("Motif d'annulation (optionnel) ?", "") ?? null; try { await api.post(`/admin/campaigns/preorders/${id}/cancel`, { reason }); toast.success("Annulée"); load(); onChanged(); } catch (e) { showError(e); } };
  const editQty = async (id, current) => {
    const raw = window.prompt("Nouvelle quantité (1..20) :", String(current || 1));
    if (raw == null) return;
    const q = Math.max(1, Math.min(20, parseInt(raw, 10) || 1));
    try { await api.patch(`/admin/campaigns/preorders/${id}`, { quantity: q }); toast.success("Quantité mise à jour"); load(); onChanged(); }
    catch (e) { showError(e); }
  };

  if (loading) return <div className="border-t p-3"><Skeleton className="h-16" /></div>;
  if (items.length === 0) return <div className="border-t p-3 text-xs text-muted-foreground">Aucune précommande.</div>;

  return (
    <div className="border-t bg-secondary/30" data-testid={`preorders-panel-${campaignId}`}>
      {/* V11.2 — Résumé par variante (quantités) */}
      {stats?.tranches?.length > 0 && (
        <div className="px-4 pt-3 pb-2 border-b bg-white/60">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Répartition T-shirts par variante</div>
          <div className="flex flex-wrap gap-2">
            {stats.tranches.map((tr) => (
              <div key={tr.tranche_number} className="rounded border p-2 text-xs bg-white flex flex-col gap-1 min-w-[180px]">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">T{tr.tranche_number}</Badge>
                  <span>{tr.confirmed_qty}/{tr.batch_size}</span>
                  <span className="text-muted-foreground font-normal">confirmés</span>
                  {tr.pending_qty > 0 && <span className="text-amber-700 font-normal">· {tr.pending_qty} en attente</span>}
                </div>
                {Object.keys(tr.by_variant || {}).length > 0 ? (
                  <div className="grid grid-cols-1 gap-0.5 text-[11px]">
                    {Object.entries(tr.by_variant).map(([variant, v]) => (
                      <div key={variant} className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground truncate">{variant}</span>
                        <span>
                          <span className="text-emerald-700 font-medium">{v.confirmed || 0}</span>
                          {(v.pending || 0) > 0 && <span className="text-amber-700"> · +{v.pending}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground">Aucune précommande</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead className="hidden md:table-cell">Email / tel</TableHead>
            <TableHead>Tranche</TableHead>
            <TableHead>Variante</TableHead>
            <TableHead className="text-center">Qté</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} data-testid={`preorder-row-${p.id}`}>
              <TableCell><div className="text-sm font-medium">{p.first_name} {p.last_name}</div></TableCell>
              <TableCell className="text-xs hidden md:table-cell"><div>{p.email}</div>{p.phone && <div className="text-muted-foreground">{p.phone}</div>}</TableCell>
              <TableCell><Badge variant="outline">T{p.tranche_number}</Badge></TableCell>
              <TableCell className="text-xs">{p.variant || "—"}</TableCell>
              <TableCell className="text-center">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:bg-secondary"
                  onClick={() => editQty(p.id, p.quantity || 1)}
                  disabled={p.status === "cancelled"}
                  title="Modifier la quantité"
                  data-testid={`preorder-qty-${p.id}`}
                >
                  <Package className="h-3 w-3" />
                  <span className="font-medium">{p.quantity || 1}</span>
                </button>
              </TableCell>
              <TableCell><Badge className={p.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : p.status === "cancelled" ? "bg-muted" : "bg-amber-100 text-amber-800"}>{p.status}</Badge></TableCell>
              <TableCell className="text-right">
                {p.status === "pending" && <Button size="sm" variant="ghost" onClick={() => confirm(p.id)} data-testid={`preorder-confirm-${p.id}`}><Check className="h-3.5 w-3.5 mr-1" />Confirmer</Button>}
                {p.status !== "cancelled" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancel(p.id)} data-testid={`preorder-cancel-${p.id}`}><X className="h-3.5 w-3.5 mr-1" />Annuler</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
